import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Papa } from 'ngx-papaparse';
import { BehaviorSubject, Observable, Subject, Subscriber } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { MarketsService } from '../core/markets/markets.service';
import { PartnerService } from '../core/partner.service';
import { BulkImportMappingService } from './bulk-import-mapping.service';
import { doNotImportOptionId } from './header-mapping.options';

export interface ErrorRowInterface {
  column: string;
  message: string;
  row: number;
  value: string;
}

export interface RetryListImportRequest {
  sessionId: string;
  actionListId: string;
  historyListId: string;
  listName: string;
  retry: boolean;
}

const ACCOUNT_CSV_IMPORT_URL = '/api/v3/accounts/bulk/import/file/';
const ACCOUNT_CSV_VALIDATE_URL = '/_ajax/v1/manage-accounts/import-list/validate-full-csv/';
const START_IMPORT_URL = '/_ajax/v1/manage-accounts/import-list/start-import/';

@Injectable()
export class BulkImportService {
  private _partnerId: string;
  private _rowNumber: number;
  private _headerRow: string[];
  private _firstRow: string[];
  private _dataSource = new MatTableDataSource();
  private accessibleMarkets: string[];

  file: File;
  errorMessage$$: Subject<string> = new Subject<string>();
  private errors$$: Subject<ErrorRowInterface[]> = new Subject<ErrorRowInterface[]>();
  errors$: Observable<ErrorRowInterface[]> = this.errors$$.asObservable();
  private errorRows$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  errorRows$: Observable<number> = this.errorRows$$.asObservable();
  private totalRows$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  totalRows$: Observable<number> = this.totalRows$$.asObservable();
  private uploading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  uploading$: Observable<boolean> = this.uploading$$.asObservable();
  sessionId: string;

  constructor(
    private http: HttpClient,
    private partnerService: PartnerService,
    private configService: AppConfigService,
    private mappingService: BulkImportMappingService,
    private marketsService: MarketsService,
    private csvParser: Papa,
  ) {}

  get partnerId(): string {
    return this._partnerId;
  }

  get dataSource(): any {
    return this._dataSource;
  }

  validateCsv(sessionId: string, filePath: string): Observable<any> {
    const body = {
      sessionId: sessionId,
      filePath: filePath,
    };
    const options = {
      withCredentials: true,
    };
    return this.http.post(ACCOUNT_CSV_VALIDATE_URL, body, options);
  }

  importAccountCsvRequest(): Observable<any> {
    const formdata = new FormData();
    this.marketsService.markets
      .pipe(
        map((markets) => {
          this.accessibleMarkets = markets.map((market) => market.market_id);
        }),
      )
      .subscribe();
    formdata.append('email', this.configService.config.userEmail);
    formdata.append('partnerId', this.partnerService.partnerId);
    formdata.append('marketIds', this.accessibleMarkets.toString());
    formdata.append('file', this.file);
    formdata.append('fieldMappings', JSON.stringify(this.mappingService.standardFieldsObject()));
    formdata.append('customFieldMappings', JSON.stringify(this.mappingService.customFieldsObject()));
    return this.http.post(ACCOUNT_CSV_IMPORT_URL, formdata, {
      withCredentials: true,
    });
  }

  processFile(): Observable<null> {
    this.errorMessage$$.next(null);
    const fileReader = new FileReader();
    fileReader.readAsText(this.file);
    return new Observable((obs) => {
      fileReader.onload = () => {
        const fileContent = fileReader.result.toString();
        this.parseFileContent(fileContent, obs);
      };
    });
  }

  parseFileContent(fileContent: string, obs: Subscriber<null>): void {
    const fileContents = this.csvParser.parse(fileContent);
    this._rowNumber = fileContents.data.length - 1;
    if (this._rowNumber <= 0) {
      obs.error('There was no content detected. The file must have one header row followed by data.');
      return;
    }
    this._headerRow = fileContents.data[0];
    this._firstRow = fileContents.data[1];
    const temp = [];
    const duplicateChecker = [];
    const duplicates = [];
    this._headerRow.forEach((row, index) => {
      const headerRow = this._headerRow[index];
      if (duplicateChecker.includes(headerRow) && !duplicates.includes(headerRow)) {
        duplicates.push(headerRow);
      }
      if (this._headerRow.length === this._firstRow.length) {
        temp.push({
          headerRow: headerRow,
          firstRow: this._firstRow[index],
          mapping: doNotImportOptionId,
        });
      }
      duplicateChecker.push(headerRow);
    });
    if (duplicates.length > 0) {
      obs.error(`These columns have the same name: ${duplicates.join(', ')}. Rename the columns and try again.`);
    }
    this._dataSource.data = temp;
    obs.next();
    obs.complete();
  }

  retryListImport(sessionId: string, actionListId: string, historyListId: string): Observable<any> {
    // TODO: Set listName as an optional field in AA for retries.  The listName here is redundant.
    const body = {
      sessionId: sessionId,
      actionListId: actionListId,
      historyListId: historyListId,
      listName: 'Retry of List Import',
      retry: true,
    } as RetryListImportRequest;

    const options = {
      withCredentials: true,
    };

    return this.http.post(START_IMPORT_URL, body, options);
  }

  startListImport(
    sessionId: string,
    listName: string,
    marketId: string,
    overwriteBlank: boolean,
    userPermissionsFlag: boolean,
    notificationsFlag: boolean,
    welcomeEmailFlag: boolean,
    emailLanguage: string,
    welcomeMessage: string,
    origin?: string,
  ): Observable<any> {
    const body = {
      sessionId: sessionId,
      listName: listName,
      marketId: marketId,
      overwriteBlankFlag: overwriteBlank,
      userPermissionsFlag: userPermissionsFlag,
      notificationsFlag: notificationsFlag,
      sendWelcomeEmailFlag: welcomeEmailFlag,
      welcomeMessage: welcomeMessage,
      preferredLocale: emailLanguage,
    };
    if (origin) {
      body['origin'] = origin;
    }
    const options = {
      withCredentials: true,
    };
    return this.http.post(START_IMPORT_URL, body, options);
  }

  uploadFileAndMapping(pageStepper: any, proceedWithErrors: boolean): void {
    const errors = this.mappingService.getMissingRequiredFields();
    if (errors.length > 0) {
      this.errorMessage$$.next('The following fields are required to be selected: ' + errors.join(', '));
      return;
    }
    this.errorMessage$$.next(null);
    this.uploading$$.next(true);
    this.importAccountCsvRequest()
      .pipe(
        take(1),
        switchMap((fileUpload) => {
          const error = fileUpload.data[0].error;
          if (error) {
            this.errorMessage$$.next(error);
            this.uploading$$.next(false);
            return;
          }
          this.sessionId = fileUpload.data[0].sessionId;
          return this.validateCsv(fileUpload.data[0].sessionId, fileUpload.data[0].filepath).pipe(
            map((x) => {
              this.uploading$$.next(false);
              this.totalRows$$.next(x.data.total_rows);
              this.errorRows$$.next(x.data.total_rows_with_error);
              if (x.data.fatal_error) {
                this.errorMessage$$.next(x.data.fatal_error?.message);
                if (!proceedWithErrors) {
                  return;
                }
              }
              this.errors$$.next(x.data.errors);
              if (x.data.errors.length > 0 && !proceedWithErrors) {
                return;
              }
              pageStepper.next();
            }),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.uploading$$.next(false);
        },
        error: () => {
          this.uploading$$.next(false);
        },
      });
  }
}
